import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Seo } from "../components/Seo";
import "./ResearchArticle.css";

const SITE_URL = "https://www.roman-storyworld.link";

const markdownFiles = import.meta.glob("../content/research/*.md", {
  query: "?raw",
  import: "default",
});

const slugAliases = {
  "caesar-first-invasion": "caesars-first-landing",
  "caesar-second-invasion": "caesars-second-expedition",
  "caratacus-captured": "caratacus-defeated-and-captured",
  "watling-street": "battle-of-watling-street",
  "agricola-governor": "agricolas-governorship",
  "commodus-britain-crisis": "crisis-on-the-northern-frontier",
  "perennis-political-shockwaves": "political-shockwaves-in-rome",
  "pertinax-british-mutiny": "pertinax-and-the-british-mutiny",
  "clodius-albinus-britain": "clodius-albinus-in-britain",
  "year-five-emperors": "year-of-five-emperors",
  "severus-in-britain": "severus-campaigns-in-britain",
  "severus-dies-york": "death-of-severus-at-eboracum",
  "division-britannia": "division-of-britannia",
  "gallic-empire-britain": "britain-in-the-gallic-empire",
  "carausius-revolt": "carausius",
  "constantius-reconquest": "constantius-reconquers-britain",
  "constantine-proclaimed": "constantine-proclaimed-at-york",
};

function getSlugFromPath(path) {
  return path.split("/").pop().replace(".md", "");
}

function parseMarkdown(raw) {
  const frontmatterMatch = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?/);
  let body = raw;
  const meta = {};

  if (frontmatterMatch) {
    body = raw.replace(frontmatterMatch[0], "");
    frontmatterMatch[1].split(/\r?\n/).forEach((line) => {
      const [key, ...rest] = line.split(":");
      if (key && rest.length) {
        meta[key.trim()] = rest.join(":").trim().replace(/^["']|["']$/g, "");
      }
    });
  }

  // Existing reports normally use their first H1 as the title rather than
  // frontmatter. Promote it to metadata and remove it from the body so that
  // each page has one clear H1.
  const firstHeading = body.match(/^#\s+(.+)$/m);
  if (!meta.title && firstHeading) {
    meta.title = firstHeading[1].trim();
    body = body.replace(firstHeading[0], "").trim();
  }

  if (!meta.description) {
    const firstParagraph = body
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line.length > 80 && !line.startsWith("#"));

    if (firstParagraph) {
      meta.description = firstParagraph
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
        .replace(/[*_`]/g, "")
        .replace(/\s+/g, " ")
        .slice(0, 280);
    }
  }

  return { meta, body };
}

function renderMarkdown(body) {
  const lines = body.split("\n");
  const elements = [];

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (!trimmed) return;

    if (trimmed.startsWith("# ")) {
      elements.push(<h1 key={index}>{trimmed.replace("# ", "")}</h1>);
    } else if (trimmed.startsWith("## ")) {
      elements.push(<h2 key={index}>{trimmed.replace("## ", "")}</h2>);
    } else if (trimmed.startsWith("### ")) {
      elements.push(<h3 key={index}>{trimmed.replace("### ", "")}</h3>);
    } else {
      elements.push(<p key={index}>{trimmed}</p>);
    }
  });

  return elements;
}

export default function ResearchArticle() {
  const { slug } = useParams();
  const actualSlug = slugAliases[slug] || slug;
  const [article, setArticle] = useState(null);
  const [error, setError] = useState("");

  const articleMap = useMemo(() => {
    const map = {};

    Object.entries(markdownFiles).forEach(([path, loader]) => {
      const fileSlug = getSlugFromPath(path);
      map[fileSlug] = loader;
    });

    return map;
  }, []);

  useEffect(() => {
    async function loadArticle() {
      setError("");
      setArticle(null);

      const loader = articleMap[actualSlug];

      if (!loader) {
        setError(`No report found for: ${slug}`);
        return;
      }

      try {
        const raw = await loader();
        setArticle(parseMarkdown(raw));
      } catch (err) {
        console.error(err);
        setError("The report could not be loaded.");
      }
    }

    loadArticle();
  }, [slug, actualSlug, articleMap]);

  if (error) {
    return (
      <main className="report-page">
        <article className="report-card">
          <p className="report-back">
            <Link to="/timeline">← Back to timeline</Link>
          </p>
          <h1>Report not found</h1>
          <p>{error}</p>
        </article>
      </main>
    );
  }

  if (!article) {
    return (
      <main className="report-page">
        <article className="report-card">
          <p>Loading report…</p>
        </article>
      </main>
    );
  }

  const title = article.meta.title || "Roman Britain Report";
  const year = article.meta.year || title.match(/^([^–—]+)[–—]/)?.[1]?.trim() || "";
  const description =
    article.meta.description ||
    `Read about ${title} and its significance in the history of Roman Britain.`;
  const articlePath = `/research/roman-britain-timeline/${actualSlug}`;
  const articleUrl = `${SITE_URL}${articlePath}`;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    url: articleUrl,
    inLanguage: "en-GB",
    author: { "@type": "Person", name: "Martin Antrobus" },
    publisher: {
      "@type": "Organization",
      name: "Roman Storyworld",
      url: `${SITE_URL}/`,
    },
    isPartOf: {
      "@type": "CollectionPage",
      name: "Roman Britain Timeline",
      url: `${SITE_URL}/timeline`,
    },
  };

  return (
    <>
      <Seo
        title={`${title} | Roman Britain Timeline`}
        description={description}
        path={articlePath}
        type="article"
        jsonLd={articleSchema}
      />
      <main className="report-page">
        <article className="report-card">
          <p className="report-back">
            <Link to="/timeline">← Back to timeline</Link>
          </p>

          {year && <p className="report-year">{year}</p>}
          <h1 className="report-main-title">{title}</h1>

          <div className="report-content">{renderMarkdown(article.body)}</div>
        </article>
      </main>
    </>
  );
}
